import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "413",
  "slug": "deepest-lakes-in-the-world",
  "title": "The Deepest Lakes in the World",
  "excerpt": "The deepest lakes on Earth plunge more than a kilometre and a half down and hold a staggering share of the planet's fresh water. Here are the deepest, and why they got that way.",
  "date": "2026-06-12",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1693818034170-27321fa95425?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxkZWVwJTIwbW91bnRhaW4lMjBsYWtlfGVufDF8MHx8fDE3ODEyNTIzMDF8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Lake Baikal in Siberia is the deepest lake on Earth at about 1,642 metres, followed by Lake Tanganyika in East Africa at about 1,470 metres. The deepest lakes are nearly all tectonic rift lakes, sitting in cracks where the Earth's crust is slowly pulling apart.",
  "content": [
    {
      "type": "paragraph",
      "text": "Most lakes are shallow. A typical lake is a few metres to a few tens of metres deep, a puddle in a hollow scooped out by ice or dammed by a ridge. A small number, though, plunge to depths that would swallow whole mountains. The deepest lakes on Earth are geological oddities, and almost all of them owe their depth to the same dramatic process."
    },
    {
      "type": "heading",
      "text": "How a lake gets that deep"
    },
    {
      "type": "paragraph",
      "text": "The deepest lakes are not carved by water at all. They sit in rift valleys, places where the Earth's crust is being slowly stretched and pulled apart, dropping a long block of land down between two faults. The basin keeps deepening over millions of years as the rift widens, and water collects in the gash. Rift lakes are typically long, narrow, and astonishingly deep. A second route to great depth is a volcanic caldera, where a collapsed magma chamber leaves a steep, round basin; a third is a glacier gouging out a deep trough and then leaving it dammed by its own debris."
    },
    {
      "type": "heading",
      "text": "Lake Baikal, Russia"
    },
    {
      "type": "paragraph",
      "text": "Lake Baikal in southern Siberia is the deepest lake on Earth, reaching about 1,642 metres at its deepest point. It is also the oldest lake in the world, at roughly 25 million years, and the largest by volume, holding close to a fifth of all the unfrozen fresh water on the planet, more than all the North American Great Lakes combined."
    },
    {
      "type": "paragraph",
      "text": "Baikal sits in an active continental rift that is still widening by a couple of centimetres a year, so the lake is, very slowly, becoming an ocean. Its isolation has produced hundreds of species found nowhere else, including the nerpa, the world's only exclusively freshwater seal. In winter the surface freezes into metre-thick, glass-clear ice that you can walk and even drive across."
    },
    {
      "type": "heading",
      "text": "Lake Tanganyika, East Africa"
    },
    {
      "type": "paragraph",
      "text": "The second deepest lake, at about 1,470 metres, is Lake Tanganyika, which stretches for some 670 kilometres along the western branch of the East African Rift, bordered by Tanzania, the Democratic Republic of the Congo, Burundi, and Zambia. It is the second largest freshwater lake by volume after Baikal and the longest freshwater lake in the world. Like Baikal, its great age and isolation have made it a hotspot of unique fish, particularly its famous cichlids, prized by aquarium keepers around the world."
    },
    {
      "type": "heading",
      "text": "The rest of the deepest"
    },
    {
      "type": "paragraph",
      "text": "Below the top two, the list is dominated by more rift and tectonic basins, with a couple of other origins mixed in:"
    },
    {
      "type": "list",
      "items": [
        "The Caspian Sea reaches about 1,025 metres, though as a vast salty inland body it is often treated separately from true freshwater lakes.",
        "Lake Vostok in Antarctica holds deep water beneath nearly four kilometres of ice, sealed off from the surface for millions of years.",
        "Lake O'Higgins-San Martin in Patagonia, on the Chile-Argentina border, is the deepest in the Americas at about 836 metres, carved and dammed by glaciers.",
        "Lake Malawi, another East African rift lake, reaches about 706 metres.",
        "Issyk-Kul in Kyrgyzstan, a high mountain lake that never freezes, reaches about 668 metres.",
        "Crater Lake in Oregon, the deepest lake in the United States at about 594 metres, fills the caldera of a collapsed volcano."
      ]
    },
    {
      "type": "heading",
      "text": "Why depth matters"
    },
    {
      "type": "paragraph",
      "text": "Great depth does more than make for impressive numbers. The deepest lakes hold enormous volumes of water, so they are vital freshwater reserves; Baikal and Tanganyika alone store a remarkable fraction of the planet's liquid fresh water. Their depth also means the bottom waters are ancient, cold, and undisturbed, which is why these lakes preserve such unusual life and why their sediments record millions of years of climate history in their layers."
    },
    {
      "type": "heading",
      "text": "What deep lakes look like from above"
    },
    {
      "type": "paragraph",
      "text": "Deep rift lakes have a recognisable signature in satellite imagery: long, narrow, and remarkably straight, often running for hundreds of kilometres in a single direction because they follow the line of a fault. Baikal's slender crescent and Tanganyika's long ribbon are both easy to pick out on a map. Caldera lakes are the opposite, near-perfect circles set into a mountain."
    }
  ]
};
